import styled, { css } from 'styled-components';

import { styledReset } from '@pkg/primitives';

import { tokens } from './tokens';
import { ChipColor, type ChipColorValue, type ChipSizeValue } from './types';

interface ChipWrapperProps {
    $color: ChipColorValue;
    $size: ChipSizeValue;
    $clickable?: boolean;
}

// TODO: Some of these resets should be scoped
// to their respective element.
export const ChipWrapper = styled.div<ChipWrapperProps>`
    ${styledReset.borderBox};

    ${({ $clickable }) =>
        $clickable &&
        css`
            ${styledReset.button};
            ${styledReset.link};
            user-select: none;

            &:disabled {
                pointer-events: none;
            }
        `};

    --yat_chip-gap: ${({ $size }) => tokens.interior.gap[$size]}px;
    --yat_chip-padding: ${({ $size }) => tokens.interior.padding[$size]}px;
    --yat_chip-height: ${({ $size }) => tokens.chip.height[$size]}px;

    --yat_chip-color: ${({ theme }) => theme.shades.white[50]};
    --yat_chip-bg-color: ${({ theme }) => theme.shades.white[5]};
    --yat_chip-border-color: transparent;

    --yat_chip-icon-size: ${({ $size }) => tokens.icon.size[$size]}px;
    --yat_chip-icon-offset: calc(var(--yat_chip-icon-size) / 4 * -1);
    --yat_chip-circle-icon-offset: calc(var(--yat_chip-icon-size) / 5 * -1);

    display: inline-flex;
    align-items: center;
    min-width: var(--yat_chip-height);
    max-width: 100%;
    height: var(--yat_chip-height);
    color: var(--yat_chip-color);
    background-color: var(--yat_chip-bg-color);
    border-radius: ${tokens.chip.height.large}px;
    transition-property: color, background-color, border-color;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    // TODO: Every variant should have interactive styling.
    ${({ theme, $color, $clickable }) => {
        if ($color === ChipColor.Default) {
            return css`
                &:focus,
                &:hover {
                    --yat_chip-bg-color: ${$clickable && theme.shades.white[20]};
                }

                &:active {
                    --yat_chip-bg-color: ${$clickable && theme.shades.white[5]};
                }
            `;
        }

        if ($color === ChipColor.Active) {
            return css`
                --yat_chip-color: ${theme.colors.white};
                --yat_chip-bg-color: ${theme.colors.purple};

                &:focus,
                &:hover {
                    --yat_chip-bg-color: ${$clickable && theme.shades.purple[70]};
                }

                &:active {
                    --yat_chip-bg-color: ${$clickable && theme.colors.purple};
                }
            `;
        }

        if ($color === ChipColor.Primary) {
            return css`
                --yat_chip-bg-color: ${theme.shades.white[10]};
            `;
        }

        if ($color === ChipColor.Secondary) {
            return css`
                --yat_chip-color: ${theme.colors.white};
                --yat_chip-bg-color: ${theme.shades.white[5]};
                --yat_chip-border-color: ${theme.shades.white[10]};

                &:focus,
                &:hover {
                    --yat_chip-bg-color: ${$clickable && theme.shades.white[10]};
                }

                &:active {
                    --yat_chip-bg-color: ${$clickable && theme.shades.white[5]};
                }
            `;
        }

        if ($color === ChipColor.Transparent) {
            return css`
                --yat_chip-bg-color: transparent;
            `;
        }

        if ($color === ChipColor.Carbon) {
            return css`
                --yat_chip-bg-color: ${theme.colors.carbon};
            `;
        }

        if ($color === ChipColor.BorderTransparent) {
            return css`
                --yat_chip-border-color: ${theme.shades.white[10]};
            `;
        }

        // TODO: This variant was added last minute and
        // should be revisited.
        if ($color === ChipColor.Shine) {
            return css`
                background:
                    linear-gradient(262deg, #232131 0%, transparent 100%) padding-box,
                    linear-gradient(206deg, rgba(255, 255, 255, 0.4) -6.9%, rgba(255, 255, 255, 0) 26.62%) border-box,
                    linear-gradient(222deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 100%) border-box,
                    #ffffff0d;

                &:focus,
                &:hover {
                    background: ${$clickable && theme.shades.white[10]};
                }
            `;
        }
        if ($color === ChipColor.Golden) {
            return css`
                background: linear-gradient(180deg, #c99839 0%, #fbcf87 82.29%), #d9d9d9;
            `;
        }

        if ($color === ChipColor.Blur) {
            return css`
                --yat_chip-color: ${theme.colors.white};
                --yat_chip-bg-color: rgb(34, 32, 48);

                @supports (backdrop-filter: blur(1px)) {
                    --yat_chip-bg-color: ${theme.shades.white[10]};
                    backdrop-filter: blur(40px);
                }

                &:focus,
                &:hover {
                    --yat_chip-bg-color: ${theme.shades.white[15]};
                }

                &:active {
                    --yat_chip-bg-color: ${theme.shades.white[10]};
                }
            `;
        }

        if ($color === ChipColor.BlurDark) {
            return css`
                --yat_chip-color: ${theme.shades.white[100]};
                --yat_chip-bg-color: rgba(0, 0, 0, 0.6);

                @supports (backdrop-filter: blur(1px)) {
                    backdrop-filter: blur(40px);
                }

                &:focus,
                &:hover {
                    --yat_chip-bg-color: ${theme.shades.white[80]};
                }

                &:active {
                    --yat_chip-bg-color: ${theme.shades.white[100]};
                }
            `;
        }
    }}
`;
